import { useEffect } from 'react';
import React from 'react';
import { LoadingSpinner } from '../../../../../../components/LoadingSpinner';
import { Col, Row } from 'reactstrap';
import { SalaryInput } from './SalaryInput';
import { SignableOfferForm } from '../..';
import { EmptyState } from '../../../../../../components/EmptyState';
import { SignableOfferTemplate } from '../../../../../../entities/v1/applicant_tracking/SignableOfferTemplate';
import { isFormValid } from './formValidator';
import { StringInput } from './StringInput';
import { NumericInput } from './NumericInput';
import { DATE_FORMAT, DateInput } from './DateInput';
import { humanizeCamelCase } from '../../../../../../utils/humanize';
import { SIGNABLE_OFFER_VARIABLES } from '../../../variables';
import moment from 'moment';
import { SignableOfferTemplateService } from '../../../../../../services/v1/applicant_tracking/SignableOfferTemplateService';
import { SignableOfferTemplateForm } from '../../../../../../entities/v1/applicant_tracking/SignableOfferTemplateForm';

interface PropTypes {
  applicationId: number;
  form: SignableOfferForm;
  offerTemplate: SignableOfferTemplate;
  variables: string[];
  setForm: (form: SignableOfferForm) => void;
  setIsValid: (valid: boolean) => void;
  setVariables: (variables: string[]) => void;
}

function VariableSelector(props: { name: string } & PropTypes) {
  switch (props.name) {
    case 'bonus':
      return (
        <SalaryInput
          label='Bonus'
          currency={props.form.bonusCurrency}
          salary={props.form.bonus}
          setCurrency={(currency: string) =>
            props.setForm({ ...props.form, bonusCurrency: currency })
          }
          setSalary={(bonus: number) =>
            props.setForm({ ...props.form, bonus: bonus })
          }
        />
      );
    case 'candidateName':
      return (
        <StringInput
          label='Candidate Name'
          value={props.form.candidateName}
          setValue={(value: string) =>
            props.setForm({ ...props.form, candidateName: value })
          }
        />
      );
    case 'companyName':
      return (
        <StringInput
          label='Company Name'
          value={props.form.companyName}
          setValue={(value: string) =>
            props.setForm({ ...props.form, companyName: value })
          }
        />
      );
    case 'directManager':
      return (
        <StringInput
          label='Direct Manager'
          value={props.form.directManager}
          setValue={(value: string) =>
            props.setForm({ ...props.form, directManager: value })
          }
        />
      );
    case 'equity':
      return (
        <NumericInput
          label='Equity'
          placeholder='Insert Equity (e.g. 1500)'
          value={props.form.equity}
          setValue={(value: number) =>
            props.setForm({ ...props.form, equity: value })
          }
        />
      );
    case 'jobTitle':
      return (
        <StringInput
          label='Job Title'
          value={props.form.jobTitle}
          setValue={(value: string) =>
            props.setForm({ ...props.form, jobTitle: value })
          }
        />
      );
    case 'offerExpirationDate':
      return (
        <DateInput
          date={props.form.offerExpirationDate}
          label='Offer Expiration Date'
          setDate={(date: string) =>
            props.setForm({ ...props.form, offerExpirationDate: date })
          }
          minDate={moment().add(1, 'day')}
        />
      );
    case 'paidTimeOff':
      return (
        <NumericInput
          label='Paid Time Off'
          placeholder='Insert Time Off (days)'
          value={props.form.paidTimeOff}
          setValue={(value: number) =>
            props.setForm({ ...props.form, paidTimeOff: value })
          }
        />
      );
    case 'salary':
      return (
        <SalaryInput
          label='Salary'
          currency={props.form.salaryCurrency}
          salary={props.form.salary}
          setCurrency={(currency: string) =>
            props.setForm({ ...props.form, salaryCurrency: currency })
          }
          setSalary={(salary: number) =>
            props.setForm({ ...props.form, salary: salary })
          }
        />
      );
    case 'senderName':
      return (
        <StringInput
          label='Sender Name'
          value={props.form.senderName}
          setValue={(value: string) =>
            props.setForm({ ...props.form, senderName: value })
          }
        />
      );
    case 'sickDays':
      return (
        <NumericInput
          label='Sick Days'
          value={props.form.sickDays}
          setValue={(value: number) =>
            props.setForm({ ...props.form, sickDays: value })
          }
        />
      );
    case 'startingDate':
      return (
        <DateInput
          date={props.form.startingDate}
          label='Starting Date'
          setDate={(date: string) =>
            props.setForm({ ...props.form, startingDate: date })
          }
          minDate={moment().add(1, 'day')}
        />
      );
    case 'todayDate':
      return (
        <DateInput
          date={props.form.todayDate}
          label='Today Date'
          setDate={(date: string) =>
            props.setForm({ ...props.form, todayDate: date })
          }
        />
      );
    case 'workingHours':
      return (
        <NumericInput
          label='Working Hours (weekly)'
          value={props.form.workingHours}
          setValue={(value: number) =>
            props.setForm({ ...props.form, workingHours: value })
          }
        />
      );
    default:
      return (
        <StringInput
          label={humanizeCamelCase(props.name)}
          value={props.form.customVariables?.get(props.name)}
          setValue={(value: string) =>
            props.setForm({
              ...props.form,
              customVariables: props.form.customVariables.set(
                props.name,
                value,
              ),
            })
          }
        />
      );
  }
}

function fixDateFormat(key: string, value: string): string {
  if (
    !value ||
    !SIGNABLE_OFFER_VARIABLES.filter((v) => v.type === 'Date')
      .map((v) => v.name)
      .includes(key)
  ) {
    return value;
  }

  if (moment(value, DATE_FORMAT, true).isValid()) return value;

  return moment(value, 'yyyy-MM-DD', true).format(DATE_FORMAT);
}

async function prefillForm(
  form: SignableOfferForm,
  offerTemplateId: number,
  applicationId: number,
): Promise<{
  variables: string[];
  prefilledForm: SignableOfferForm;
}> {
  const response = await SignableOfferTemplateService.formDefinition(
    offerTemplateId,
    applicationId,
  );

  const prefilledForm: Partial<SignableOfferForm> = {};
  const variables = Object.entries(prefillExistingForm(response, form).form)
    .map(([key, value]) => {
      const variable = SIGNABLE_OFFER_VARIABLES.find((v) => v.name === key);

      if (!variable || variable.form === true) {
        prefilledForm[key] = fixDateFormat(key, value as unknown as string);
        return key;
      }

      return null;
    })
    .filter((v): v is string => v != null);

  return {
    variables,
    prefilledForm: prefilledForm as SignableOfferForm,
  };
}

function prefillExistingForm(
  chosenForm: SignableOfferTemplateForm,
  existingForm: SignableOfferForm,
): SignableOfferTemplateForm {
  const variables = Array.from(existingForm.customVariables.entries());
  const existingVariables = Object.fromEntries(
    variables.filter((keyValuePair) => keyValuePair[1] !== null),
  );

  for (const [key, value] of Object.entries(chosenForm.form)) {
    if (value) continue;

    const newValue = existingForm[key] ?? existingVariables[key];
    if (newValue == null) continue;

    chosenForm.form[key] = fixDateFormat(key, newValue as string);
  }

  return chosenForm;
}

export function OfferDetails(props: PropTypes) {
  useEffect(() => {
    (async () => {
      if (props.variables == null) {
        const { variables, prefilledForm } = await prefillForm(
          props.form,
          props.offerTemplate.id,
          props.applicationId,
        );

        props.setVariables(variables);
        props.setForm({ ...props.form, ...prefilledForm });
      }
    })();
  }, []);

  useEffect(() => {
    props.setIsValid(isFormValid(props.form, props.variables));
  }, [props.form, props.variables]);

  if (props.variables == null) {
    return <LoadingSpinner />;
  }

  return props.variables.length > 0 ? (
    <>
      <Row className='px-5 g-4'>
        {props.variables.map((v, index) => (
          <Col key={index} xs='6'>
            <VariableSelector {...props} name={v} />
          </Col>
        ))}
      </Row>
    </>
  ) : (
    <EmptyState
      classNames='my-0'
      cardless
      title={'There are no variables to setup'}
    />
  );
}
